/**
 * BrowserBusStatusPill — small visual indicator of SSE connection
 * health.
 *
 * Subscribes to the same EventSource events useBrowserBus listens to.
 * Three states:
 *   - "connecting": initial / reconnecting (faint dot)
 *   - "live": SSE delivered hello + recent ping (green dot)
 *   - "stale": no events received in 60s (yellow dot)
 *   - "dropped": EventSource errored / closed (red dot)
 *
 * Without this, FB-MOW4PACV bites: list_open_browser_windows reports
 * the tab as registered but the user has no way to know if the SSE
 * pipe is delivering events. Now they can see "live" → if the AI's
 * choreography events still don't render, the issue is downstream.
 */

import { useEffect, useState } from "react";

type PillState = "connecting" | "live" | "stale" | "dropped";

const STATE_STYLES: Record<PillState, { dot: string; label: string; title: string }> = {
  connecting: { dot: "bg-stone-300", label: "connecting", title: "Establishing live connection…" },
  live: { dot: "bg-green-500", label: "live", title: "Live link to AI clients — events are streaming" },
  stale: { dot: "bg-amber-500", label: "idle", title: "No events received in 60s — connection may be stale" },
  dropped: { dot: "bg-red-500", label: "offline", title: "Live connection dropped — refresh to reconnect" },
};

export function BrowserBusStatusPill() {
  const [state, setState] = useState<PillState>("connecting");

  useEffect(() => {
    let lastEventAt = Date.now();
    let staleTimer: ReturnType<typeof setInterval> | null = null;

    const markEvent = () => {
      lastEventAt = Date.now();
      // EventSource reconnects automatically. A fresh hello/ping is proof the
      // new stream is healthy, so recover from the dropped state immediately.
      setState("live");
    };

    const onHello = () => markEvent();
    const onPing = () => markEvent();
    const onChoreo = () => markEvent();
    const onDataChanged = () => markEvent();
    const onDropped = () => setState("dropped");

    window.addEventListener("cmcp:browser-bus-hello", onHello);
    window.addEventListener("cmcp:browser-bus-ping", onPing);
    window.addEventListener("cmcp:browser-bus-choreography", onChoreo);
    window.addEventListener("cmcp:browser-bus-data-changed", onDataChanged);
    window.addEventListener("cmcp:browser-bus-dropped", onDropped);

    staleTimer = setInterval(() => {
      const age = Date.now() - lastEventAt;
      if (age > 60_000) setState((prev) => (prev === "dropped" ? prev : "stale"));
    }, 5_000);

    return () => {
      window.removeEventListener("cmcp:browser-bus-hello", onHello);
      window.removeEventListener("cmcp:browser-bus-ping", onPing);
      window.removeEventListener("cmcp:browser-bus-choreography", onChoreo);
      window.removeEventListener("cmcp:browser-bus-data-changed", onDataChanged);
      window.removeEventListener("cmcp:browser-bus-dropped", onDropped);
      if (staleTimer) clearInterval(staleTimer);
    };
  }, []);

  const s = STATE_STYLES[state];
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-stone-100 text-stone-600 text-[10px] font-mono"
      title={s.title}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} aria-hidden />
      <span>{s.label}</span>
    </span>
  );
}
